(() => {
  function initBundle(root) {
    if (root.dataset.bundleInitialized === 'true') return;

    const cfgEl = root.querySelector('[data-bundle-config]');
    if (!cfgEl) return;

    let cfg;
    try {
      cfg = JSON.parse(cfgEl.textContent);
    } catch (error) {
      return;
    }

    root.dataset.bundleInitialized = 'true';

    const labels = cfg.labels || {};
    // Read the initially selected purchase mode from the markup rather than assuming one-time:
    // Liquid preselects Automatic delivery whenever a Juo selling plan exists, and falls back to
    // one-time when it does not. Hardcoding a default here would disagree with the checked radio.
    const preselected = root.querySelector('[data-purchase-option] input[type="radio"]:checked');
    const defaultPurchase = preselected ? preselected.value : 'one-time';
    // The subscription card is hidden below the minimum box count, which forces one-time. Once it
    // comes back the default must come back with it - but only while the customer has not chosen
    // for themselves, otherwise adding a box would silently overwrite their pick.
    //
    // purchaseChoice is what the customer CLICKED (client 2026-09-27), not what the page shows:
    //   null            never chose - the page shows its default (Automatische levering) at 2+ boxes
    //   'subscription'  their own pick, restored whenever the card unlocks again
    //   'one-time'      their own "no" - a click on Eenmalige aankoop, or the drawer switch turned off
    //                   (read back from the cart's _cowmit_sub_optout attribute)
    // The one-time forced below the unlock count is not a choice and leaves this alone. It replaces
    // a yes/no flag that stored only THAT a choice was made, and was also set just by reading a
    // subscription cart - so after a reload, 3 boxes -> 1 -> 3 came back on the forced one-time and
    // stayed there, and the next write told the drawer the customer had declined.
    let purchaseChoice = null;
    const state = { qty: {}, purchase: defaultPurchase, planId: null };
    cfg.flavours.forEach((flavour) => { state.qty[flavour.key] = 0; });

    // ---- What the customer built, remembered by the page itself -------------------------
    // The cart cannot be trusted for a few seconds after a checkout return: Juo undoes the
    // plan swap with a clear followed by a re-add, and that pair was measured at 3.3s end to
    // end on the live store, with other cart writes overlapping it. Reads taken inside it come
    // back empty, and three rounds of guards trying to judge WHICH empty reads were real all
    // eventually lost the race - because the question is unanswerable while two scripts are
    // rewriting the same cart.
    //
    // So stop asking. This records what the customer actually did, and only what they did:
    // it is written from the input handlers, never from a cart read, so a bad read can never
    // launder itself into the record. The cart stays the source of truth everywhere else.
    const STORE_KEY = 'cowmit-bundle:' + (root.id || 'default');
    // How long after a checkout handoff the remembered state outranks an empty cart.
    const HANDOFF_TRUST_MS = 20000;
    // Anything older than this is a stale tab, not a journey in progress.
    const STORE_MAX_AGE_MS = 30 * 60 * 1000;
    let trustPersistedUntil = 0;

    function persistState(options) {
      try {
        sessionStorage.setItem(STORE_KEY, JSON.stringify({
          qty: state.qty,
          purchase: state.purchase,
          planId: state.planId,
          purchaseChoice: purchaseChoice,
          handoff: !!(options && options.handoff),
          at: Date.now()
        }));
      } catch (_error) { /* private mode, or storage full - the page still works without it */ }
    }

    function readPersisted() {
      try {
        const raw = sessionStorage.getItem(STORE_KEY);
        if (!raw) return null;
        const saved = JSON.parse(raw);
        if (!saved || typeof saved !== 'object' || !saved.qty) return null;
        if (Date.now() - Number(saved.at || 0) > STORE_MAX_AGE_MS) return null;
        return saved;
      } catch (_error) { return null; }
    }

    function restorePersisted() {
      const saved = readPersisted();
      if (!saved) return false;
      let total = 0;
      cfg.flavours.forEach((flavour) => {
        const value = Math.max(0, Math.min(9, Number(saved.qty[flavour.key]) || 0));
        state.qty[flavour.key] = value;
        total += value;
      });
      if (!total) return false;
      if (saved.purchase === 'subscription' || saved.purchase === 'one-time') {
        state.purchase = saved.purchase;
      }
      if (saved.planId) state.planId = Number(saved.planId) || null;
      if (saved.purchaseChoice === 'subscription' || saved.purchaseChoice === 'one-time') {
        purchaseChoice = saved.purchaseChoice;
      } else if (saved.purchaseTouched && saved.purchase === 'one-time') {
        purchaseChoice = 'one-time';   // stored by the previous version of this file
      }
      // Only a handoff earns the right to outrank the cart, and only briefly. A plain reload
      // restores the numbers but lets the very next cart read correct them as usual.
      if (saved.handoff) trustPersistedUntil = Date.now() + HANDOFF_TRUST_MS;
      return true;
    }

    const summary = root.querySelector('[data-summary]');
    const summaryToggle = root.querySelector('[data-summary-toggle]');
    const summaryPanel = root.querySelector('[data-summary-panel]');
    const checkoutButton = root.querySelector('[data-checkout-btn]');
    const checkoutError = root.querySelector('[data-checkout-error]');
    const checkoutCtas = root.querySelector('[data-bundle-checkout-ctas]');
    const checkoutOnetime = root.querySelector('[data-checkout-onetime]');
    const checkoutSubscription = root.querySelector('[data-checkout-subscription]');
    const juoCheckoutLink = root.querySelector('juo-checkout-link');
    const intervalSelect = root.querySelector('[data-interval-select]');
    const intervalDropdown = root.querySelector('[data-interval-dropdown]');
    const intervalToggle = root.querySelector('[data-interval-toggle]');
    const intervalLabel = root.querySelector('[data-interval-label]');
    const intervalMenu = root.querySelector('[data-interval-menu]');
    const deliverySummary = root.querySelector('[data-delivery-summary]');
    const subscriptionOption = root.querySelector('[data-purchase-option="subscription"]');

    const totalBoxes = () => Object.values(state.qty).reduce((total, qty) => total + qty, 0);

    function discountCents(quantity) {
      if (quantity >= 3) return Number(cfg.discount3Cents) || 0;
      if (quantity === 2) return Number(cfg.discount2Cents) || 0;
      return 0;
    }

    function subtotalCents() {
      return cfg.flavours.reduce((sum, flavour) => {
        return sum + Number(flavour.priceCents || 0) * state.qty[flavour.key];
      }, 0);
    }

    function totalCents() {
      return Math.max(0, subtotalCents() - discountCents(totalBoxes()));
    }

    function euro(cents, decimals) {
      const value = Number(cents || 0) / 100;
      return decimals
        ? '€' + value.toFixed(2).replace('.', ',')
        : '€' + Math.round(value);
    }

    function tierPrice(tier) {
      const base = Number(cfg.boxPriceCents) || Number(cfg.flavours[0]?.priceCents) || 0;
      const discount = tier === 2
        ? Number(cfg.discount2Cents) || 0
        : tier === 3
          ? Number(cfg.discount3Cents) || 0
          : 0;
      return euro(((base * tier) - discount) / (tier * cfg.barsPerBox), true) + ' / ' + (labels.perBar || '');
    }

    function setText(selector, value) {
      const element = root.querySelector(selector);
      if (element) element.textContent = value;
    }

    function renderTiers(quantity) {
      const tierTrack = root.querySelector('.cowmit-bundle__tiers');
      if (tierTrack) tierTrack.dataset.tierProgress = String(Math.min(Math.max(quantity, 0), 3));

      root.querySelectorAll('[data-tier-node]').forEach((node) => {
        const tier = Number(node.dataset.tierNode);
        node.classList.remove('is-active');
        node.classList.toggle('is-done', quantity >= tier);
      });

      [1, 2, 3].forEach((tier) => setText(`[data-tier-price="${tier}"]`, tierPrice(tier)));
    }

    function renderFlavours() {
      cfg.flavours.forEach((flavour) => {
        const row = root.querySelector(`[data-flavour-row][data-key="${flavour.key}"]`);
        if (!row) return;
        const qty = state.qty[flavour.key];
        row.classList.toggle('has-qty', qty > 0);
        const output = row.querySelector('[data-qty-input]');
        if (output) output.textContent = qty;
      });
    }

    function createSummaryRow(flavour, qty) {
      const row = document.createElement('div');
      row.className = 'cowmit-bundle__summary-row';

      const imageWrap = document.createElement('span');
      imageWrap.className = 'cowmit-bundle__summary-row-image-wrap';
      if (flavour.image) {
        const image = document.createElement('img');
        image.className = 'cowmit-bundle__summary-row-image';
        image.src = flavour.image;
        image.alt = flavour.title || '';
        image.width = 236;
        image.height = 168;
        image.loading = 'lazy';
        imageWrap.appendChild(image);
      }

      const copy = document.createElement('span');
      copy.className = 'cowmit-bundle__summary-row-copy';
      const title = document.createElement('strong');
      title.textContent = flavour.title || '';
      const subline = document.createElement('small');
      subline.textContent = flavour.subline || '';
      copy.append(title, subline);

      const quantity = document.createElement('strong');
      quantity.className = 'cowmit-bundle__summary-row-qty';
      quantity.textContent = `${qty * cfg.barsPerBox} ${labels.bars || ''}`.trim();

      row.append(imageWrap, copy, quantity);
      return row;
    }

    function renderSummary(quantity, total) {
      const rows = root.querySelector('[data-summary-rows]');
      if (rows) {
        rows.replaceChildren();
        cfg.flavours.forEach((flavour) => {
          const qty = state.qty[flavour.key];
          if (qty) rows.appendChild(createSummaryRow(flavour, qty));
        });
      }

      setText('[data-summary-title]', `${quantity} ${labels.boxesSelected || ''}`.trim());
      setText('[data-summary-bars]', `${quantity * cfg.barsPerBox} ${labels.bars || ''}`.trim());
      setText('[data-summary-total]', euro(total));
      setText('[data-summary-panel-total]', euro(total));

      const boxLabel = quantity === 1 ? labels.boxSingular : labels.boxPlural;
      setText(
        '[data-summary-total-meta]',
        `${quantity} ${boxLabel || ''} · ${quantity * cfg.barsPerBox} ${labels.bars || ''}`.trim()
      );

      const shipping = root.querySelector('[data-free-shipping]');
      if (shipping) shipping.hidden = quantity < 2;
      setText('[data-saving]', `${labels.save || ''} ${euro(discountCents(quantity))}`.trim());
    }

    function renderPurchase(total) {
      root.querySelectorAll('[data-purchase-option]').forEach((option) => {
        const selected = option.dataset.purchaseOption === state.purchase;
        option.classList.toggle('is-selected', selected);
        const radio = option.querySelector('input[type="radio"]');
        if (radio) radio.checked = selected;
      });

      setText('[data-purchase-price="one-time"]', euro(total));
      setText('[data-purchase-price="subscription"]', euro(total));
      setText('[data-checkout-total]', euro(total));
    }

    function selectedPlanName() {
      if (!intervalSelect) return '';
      return intervalSelect.options[intervalSelect.selectedIndex]?.textContent?.trim() || '';
    }

    // The option label now carries the rate after a dash ("Elke 5 weken - geschikt voor ..."),
    // but the dropdown TRIGGER shows only the interval, so cut at the first separator. Derived
    // from the rendered text rather than from data so it follows whatever the client types into
    // the label format, as long as the interval comes first.
    function normalizedInterval(planName) {
      return String(planName || '')
        .replace(/^elke\s+/i, '')
        .split(/\s*[—–,;(]|\s+-\s+/)[0]
        .replace(/\s*\([^)]*\)\s*$/, '')
        .trim();
    }

    function intervalWeeks(planName) {
      const match = String(planName || '').match(/(\d+)\s*(?:week|weken)/i);
      return match ? Number(match[1]) : 0;
    }

    // ---- Delivery-frequency table (client 2026-08-26) -------------------------------------
    // Juo exposes ten frequencies, but the customer may only ever see THREE, and which three
    // depends on how many boxes are in the bundle. The table is settings-driven, not derived:
    // the Light and Heavy lanes happen to follow 3n-1 and n, but the Daily lane is hand-tuned by
    // the client (2 boxes = 24 bars over 3 weeks, not the 5 weeks 24/7 would give), so a formula
    // would silently disagree with the sheet they signed off. Every value stays editable.
    const freqCfg = cfg.frequency || {};
    const freqMinBoxes = Math.max(1, Number(freqCfg.minBoxes) || 2);
    const freqFormat = String(freqCfg.format || '[weeks] weken - [desc]');

    function parseWeekList(value) {
      return String(value || '')
        .split(',')
        .map((entry) => Number(String(entry).trim()))
        .filter((weeks) => Number.isFinite(weeks) && weeks > 0);
    }

    // `index` is the lane's position in the config (0 light, 1 daily, 2 heavy), taken BEFORE the
    // filter so an empty lane cannot shift the others. It is what the _cowmit_sub_lane cart
    // attribute stores, and the cart drawer's switch reads it to keep the customer on their lane.
    const freqLanes = (Array.isArray(freqCfg.lanes) ? freqCfg.lanes : []).map((lane, index) => ({
      index,
      weeks: parseWeekList(lane.weeks),
      desc: String(lane.desc || ''),
      recommended: lane.recommended === true
    })).filter((lane) => lane.weeks.length > 0);

    // Rows for a given box count. Counts past the end of the table reuse the LAST column rather
    // than dropping the subscription: the lists are the client's, and extending them is an edit
    // in the theme editor, not a code change.
    function frequencyRows(boxes) {
      if (boxes < freqMinBoxes || !freqLanes.length) return [];
      const rows = [];
      freqLanes.forEach((lane) => {
        // Counts past the end of the table reuse the LAST column rather than dropping the
        // subscription: the lists are the client's, and extending them is an edit in the theme
        // editor, not a code change.
        const index = Math.min(boxes - freqMinBoxes, lane.weeks.length - 1);
        const weeks = lane.weeks[index];
        if (!weeks) return;
        // Match the row to a real Juo selling plan by the week count in its name. No match means
        // the client has not created that frequency yet, so the row is dropped rather than
        // rendered as an option that cannot check out.
        const plan = uniquePlans.find((candidate) => intervalWeeks(candidate.name) === weeks);
        if (!plan) return;
        rows.push({ weeks, desc: lane.desc, recommended: lane.recommended, lane: lane.index, plan });
      });
      // Two lanes can land on the same interval at the low end - at 1 box both Daily and Heavy
      // want a sub-2-week delivery, and 2 weeks is the shortest frequency Juo offers. Show that
      // interval once, keeping the recommended lane's wording for it.
      const winner = new Map();
      rows.forEach((row) => {
        const held = winner.get(row.weeks);
        if (!held || (row.recommended && !held.recommended)) winner.set(row.weeks, row);
      });
      return rows.filter((row) => winner.get(row.weeks) === row);
    }

    function frequencyLabel(row) {
      return freqFormat
        .replaceAll('[weeks]', String(row.weeks))
        .replaceAll('[desc]', row.desc)
        .trim();
    }

    let lastFrequencyKey = null;

    function setLocked(locked, remaining) {
      subscriptionOption?.classList.toggle('is-locked', locked);
      const radio = subscriptionOption?.querySelector('input[type="radio"]');
      if (radio) radio.disabled = locked;
      if (intervalToggle) {
        intervalToggle.disabled = locked;
        intervalToggle.setAttribute('aria-disabled', String(locked));
      }
      if (!locked) return;
      // Countdown, not a rule: "Nog 1 doos" tells the customer exactly what to do and how close
      // they are, which converts better than restating the minimum.
      const unit = remaining === 1 ? labels.boxSingular : labels.boxPlural;
      if (intervalLabel) {
        intervalLabel.textContent = String(freqCfg.lockLabel || '')
          .replaceAll('[n]', String(remaining))
          .replaceAll('[unit]', unit || '')
          .trim();
      }
      if (deliverySummary) {
        deliverySummary.textContent = String(freqCfg.lockNote || '')
          .replaceAll('[min]', String(freqMinBoxes))
          .trim();
        alignIntervalToSummary();
      }
    }

    function renderFrequency(quantity) {
      if (!intervalSelect) return;

      // Emptying the bundle is starting over, so a previous choice stops applying. Without this,
      // someone who once declined the subscription could clear everything, build a fresh bundle,
      // and still land on one-time instead of the Automatische levering default. Dropping to 1 box
      // is NOT starting over: the bundle still exists, so the choice survives that.
      if (quantity === 0) purchaseChoice = null;

      const rows = frequencyRows(quantity);

      // A store with no selling plans at all has no subscription to offer, so the card goes away
      // entirely. That is a different situation from "not enough boxes yet", which locks instead.
      const noPlansAtAll = uniquePlans.length === 0;
      if (subscriptionOption) subscriptionOption.hidden = noPlansAtAll;

      if (!rows.length) {
        // Client 2026-08-26: show the card but LOCK it, rather than hiding it - the perks are the
        // sell, and a countdown to the unlock is a clearer prompt than a card that vanishes.
        // The radio is disabled along with the dropdown: a selectable subscription with no plan
        // behind it would check out silently as a ONE-TIME order.
        state.planId = null;
        if (state.purchase === 'subscription') state.purchase = 'one-time';
        setLocked(true, Math.max(freqMinBoxes - quantity, 0));
        intervalSelect.replaceChildren();
        intervalMenu?.replaceChildren();
        closeIntervalMenu();
        // Forget the cached option set too. Emptying the dropdown without clearing this let a
        // customer who went 2 boxes -> 1 -> 2 land back on an identical key, short-circuit the
        // rebuild below, and get an unlocked card with an EMPTY dropdown and no plan id.
        lastFrequencyKey = null;
        return;
      }
      setLocked(false, 0);

      // Unlocked: show what the customer picked, or the default if they never did. Always, not only
      // when untouched - the one-time forced while locked must not outlive the lock.
      state.purchase = purchaseChoice || defaultPurchase;

      const key = rows.map((row) => row.weeks + ':' + row.plan.id).join('|');
      if (key === lastFrequencyKey) return;
      lastFrequencyKey = key;

      intervalSelect.replaceChildren();
      rows.forEach((row) => {
        const option = document.createElement('option');
        option.value = row.plan.id;
        option.textContent = frequencyLabel(row);
        option.dataset.weeks = String(row.weeks);
        option.dataset.rate = row.desc;
        option.dataset.lane = String(row.lane);
        intervalSelect.appendChild(option);
      });

      // The recommended (daily) lane is the default, and it is re-applied whenever the box count
      // changes the option set - the customer's old pick is a different plan at the new count.
      const recommended = rows.find((row) => row.recommended) || rows[0];
      intervalSelect.value = String(recommended.plan.id);
      state.planId = Number(intervalSelect.value) || null;
      buildIntervalDropdown();
      closeIntervalMenu();
    }

    function selectedFrequencyOption() {
      if (!intervalSelect || intervalSelect.selectedIndex < 0) return null;
      return intervalSelect.options[intervalSelect.selectedIndex] || null;
    }

    function renderDelivery(quantity) {
      // The locked state owns both strings; renderFrequency has already written them.
      if (subscriptionOption?.classList.contains('is-locked')) return;
      const planName = selectedPlanName();
      const interval = normalizedInterval(planName);
      const bars = quantity * cfg.barsPerBox;

      if (intervalLabel) {
        intervalLabel.textContent = interval && bars
          ? `${interval} (${bars} ${labels.bars || ''})`.trim()
          : interval;
      }

      if (!deliverySummary) return;
      // No interval, or an empty bundle: the card is on screen at every box count now, so with
      // nothing chosen the sentence would read "elke 2 weken 0 repen". Leave it blank until the
      // customer has picked something for it to describe.
      if (!interval || !bars) {
        deliverySummary.textContent = '';
        alignIntervalToSummary();
        return;
      }

      let summary = String(labels.deliverySummary || '')
        .replaceAll('[interval]', interval.toLowerCase())
        .replaceAll('[bars]', String(bars));
      // [rate] is the client's own wording for the chosen row of the frequency table. Prefer it
      // over the computed [per_day] figure, which rounds (24 bars over 5 weeks is 0.69 a day but
      // renders as 1) and so contradicts the option the customer just picked.
      const rate = selectedFrequencyOption()?.dataset.rate || '';
      if (summary.includes('[rate]')) {
        summary = summary.replaceAll('[rate]', rate);
      } else if (rate) {
        // A template saved before [rate] existed - or an untouched Translate & Adapt entry - still
        // carries the "(an average of [per_day] ...)" clause. Swap that whole clause for the row's
        // wording so the sentence cannot disagree with the dropdown, whatever the saved string is.
        summary = summary.replace(/\s*\([^)]*\[per_day\][^)]*\)/, ' (' + rate + ')');
      }
      const weeks = intervalWeeks(planName);
      if (weeks > 0) {
        const perDay = bars > 0 ? Math.max(1, Math.round(bars / (weeks * 7))) : 0;
        const perDayUnit = perDay === 1 ? labels.perBar : labels.bars;
        summary = summary
          .replaceAll('[per_day]', String(perDay))
          .replaceAll('[per_day_unit]', perDayUnit || '');
      } else {
        summary = summary
          .replace(/\s*\([^)]*\[per_day\][^)]*\)/, '')
          .replaceAll('[per_day]', '')
          .replaceAll('[per_day_unit]', '');
      }
      deliverySummary.textContent = summary.trim();
      alignIntervalToSummary();
    }

    // Client 2026-08-25: the interval control should end where the summary sentence ends, not at the
    // panel's edge. That target is a TEXT extent, which CSS cannot reference, so measure it.
    // Guarded: only when the sentence occupies a single line - when it wraps (narrow screens) the
    // last line is short and matching it would squash the control, so it keeps its full width.
    function alignIntervalToSummary() {
      const panel = root.querySelector('.cowmit-bundle__subscription-details');
      if (!panel || !deliverySummary) return;
      // Locked: the note is a short one-liner, and matching the row to it shrank the field to a
      // stub beside "Lever elke". Let the locked row span the panel like the unlocked dropdown
      // does, so the two states have the same footprint.
      if (subscriptionOption?.classList.contains('is-locked')) {
        panel.style.removeProperty('--delivery-w');
        return;
      }
      // Clear before measuring, otherwise we would measure the previously constrained width.
      panel.style.removeProperty('--delivery-w');
      if (!deliverySummary.textContent.trim()) return;
      let lines;
      try {
        const range = document.createRange();
        range.selectNodeContents(deliverySummary);
        lines = range.getClientRects();
      } catch (error) {
        return;
      }
      // Only when the sentence is a single line. Wrapped (narrow screens), its last line is short
      // and matching it would squash the row, so the panel keeps its full width.
      if (!lines || lines.length !== 1) return;
      const width = Math.ceil(lines[0].width) + 1; // +1 guards sub-pixel rounding causing a wrap
      // Both the delivery row and the sentence take this width and are centred, so the group has
      // equal space either side instead of hugging the left edge.
      if (width > 120) panel.style.setProperty('--delivery-w', width + 'px');
    }

    function render() {
      const quantity = totalBoxes();
      const total = totalCents();
      renderTiers(quantity);
      renderFlavours();
      renderSummary(quantity, total);
      // Before renderPurchase: it paints the selected radio, and renderFrequency can force the
      // customer back to one-time when the box count leaves no valid subscription frequency.
      // (Also auto-selects subscription once enough boxes unlock the plan — read purchase AFTER.)
      renderFrequency(quantity);
      renderPurchase(total);
      renderDelivery(quantity);

      const isSubscription = state.purchase === 'subscription';
      if (checkoutButton) checkoutButton.disabled = quantity === 0;
      if (checkoutCtas) checkoutCtas.classList.toggle('is-subscription', isSubscription);
      if (checkoutOnetime) checkoutOnetime.hidden = isSubscription;
      if (checkoutSubscription) checkoutSubscription.hidden = !isSubscription;
      if (juoCheckoutLink) {
        juoCheckoutLink.setAttribute('aria-disabled', quantity === 0 ? 'true' : 'false');
        juoCheckoutLink.setAttribute('tabindex', quantity === 0 ? '-1' : '0');
      }
    }

    // ---- Cart-derived state (client 2026-08-26) -------------------------------------------
    // The builder used to keep its state in memory only, so leaving the page and coming back reset
    // every quantity to zero - the progress bar emptied and the subscription card re-locked - even
    // though the boxes were still sitting in the cart, visible in the drawer. The two disagreed.
    // The cart is the source of truth, so the page reads it back instead of storing a private copy:
    // it survives a full reload, the back button and a new tab, and it stays correct when the
    // customer edits quantities in the drawer.
    const ourVariantIds = new Set(cfg.flavours.map((flavour) => String(flavour.variantId)));

    async function readCart() {
      try {
        const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (!response.ok) return null;
        return await response.json();
      } catch (error) {
        // Offline, or the endpoint is unavailable in a preview context. The page still works from
        // an empty state, so a failed read must never block rendering.
        return null;
      }
    }

    // Consecutive reads that did not come back at all. Distinct from emptyReadRetries, which counts
    // reads that succeeded and reported nothing of ours - a different claim entirely.
    let failedReadRetries = 0;

    // Cart writes currently in flight from ANY script on the page, and when the last one settled.
    // Measured on the live store: Juo's revert issues /cart/clear (808ms) then /cart/add.js
    // (902ms), so the cart is genuinely empty for ~900ms and reads land inside that window.
    let pendingCartWrites = 0;
    let lastCartWriteAt = 0;
    // Absolute cap so a page that never stops writing cannot retry for ever.
    let suspectEmptyReads = 0;

    // Signature of the cart's OWN builder lines the last time we looked. A live sync acts only
    // when this changes - see the guard in syncFromCart for why that matters.
    let lastCartSignature = null;

    // How many times in a row a read has come back with none of our lines while the page still
    // shows some. Reset by any read that finds them, so it only ever counts a single burst.
    let emptyReadRetries = 0;

    // givenCart: a cart a write has just answered with (see watchCart) - used instead of reading.
    async function syncFromCart(isLive, givenCart) {
      // A write is remove-then-add, so between those two calls the cart is briefly EMPTY of our
      // lines. A sync landing in that window read the empty cart, believed it, and reset the page
      // to zero - the "add 2, it jumps back" report. Measured on the live page: /cart/update.js at
      // 3310ms, a sync reading at 3401ms, /cart/add.js only at 3815ms.
      // While any write is pending, running, or being painted, the PAGE is the truth and whatever
      // the cart says right now is a half-finished picture of it. Checked before the fetch so a
      // suppressed sync costs no request either.
      if (isLive && (writeTimer || inFlightWrites > 0 || refreshingUi)) return;
      // Checkout is mid-handoff: the cart is being rewritten by the swap and is not a picture of
      // anything the customer chose. Applies to the FIRST read too, not just live ones - a restore
      // landing here would wipe the page just as surely.
      if (checkoutHandoff) return;
      // Never two reads at once. A burst of drawer activity fires several triggers in quick
      // succession; without this they each opened their own request and queued behind each other.
      // A cart handed over from a write's answer is newer than any read in flight, so it goes ahead;
      // the read, when it lands, finds the page changed under it and discards itself.
      if (isLive && syncInFlight && !givenCart) return;

      // What the page showed when this read began. The read takes ~330ms against live Shopify, and
      // the customer can act inside that window - which is exactly the reported fault: return to
      // the page, click + straight away, and watch 3 become 4 and then snap back to 3 as the
      // restore landed on top of the click.
      const pageBeforeRead = stateSignature();

      let cart = givenCart || null;
      if (!cart) {
        syncInFlight = true;
        try {
          cart = await readCart();
        } finally {
          syncInFlight = false;
        }
      }

      // A read that FAILED is not a cart that is empty, and the two used to end the same way: one
      // `return` and the page kept whatever it was showing. On a fresh load that is zero boxes,
      // so a single unanswered /cart.js left the builder blank while the header bubble - rendered
      // server-side, needing no request - still read 2. Cart and page disagreed, permanently,
      // because nothing ever asked again.
      //
      // Shopify throttles /cart.js, and this page is unusually good at provoking it: every
      // quantity change writes the cart, the checkout handoff clears and re-adds, and Juo's revert
      // clears and re-adds once more. A burst of that within a few seconds - which is exactly the
      // build-then-checkout-then-back journey - can come back HTTP 429 with an HTML body. Observed
      // directly while testing this page: status 429, content-type text/html.
      //
      // So retry, with backoff, and let a genuinely empty cart still be handled below by code that
      // knows it got an answer.
      if (!cart || !Array.isArray(cart.items)) {
        if (failedReadRetries < 4) {
          failedReadRetries += 1;
          scheduleCartSync(600 * failedReadRetries);
        }
        return;
      }
      failedReadRetries = 0;

      // Re-checked AFTER the read, not just before it, and for the first restore too - that one
      // passes isLive false and so skipped the guard above entirely, leaving the whole initial
      // read unprotected. If the customer touched anything while the cart was in flight, their
      // input is newer than this response: keep the page as they left it and let the pending write
      // carry it to the cart. A value must never jump backwards.
      if (stateSignature() !== pageBeforeRead) return;
      if (writeTimer || inFlightWrites > 0 || refreshingUi) return;

      const quantities = {};
      cfg.flavours.forEach((flavour) => { quantities[flavour.key] = 0; });
      let planId = null;
      let planWeeks = 0;

      cart.items.forEach((line) => {
        const flavour = cfg.flavours.find((f) => String(f.variantId) === String(line.variant_id));
        if (!flavour) return;
        // Option A (client's choice): mirror the whole cart, so a box added from a product page
        // counts here too and the builder and the drawer always tell the same story.
        quantities[flavour.key] += Number(line.quantity) || 0;
        const plan = line.selling_plan_allocation && line.selling_plan_allocation.selling_plan;
        if (plan && planId === null) {
          planId = Number(plan.id) || null;
          planWeeks = intervalWeeks(plan.name);
        }
      });

      const signature = JSON.stringify(quantities) + '|' + String(planId);
      const total = Object.values(quantities).reduce((sum, qty) => sum + qty, 0);

      // An empty cart is CONFIRMED before it is believed, never acted on first sight.
      //
      // Juo's module reverts the checkout swap by clearing the cart and re-adding it, and it does
      // that from getCartService() - so it runs on the FIRST PAGE LOAD after a checkout, not only
      // on a bfcache restore. (It no-ops unless some line carries _selling_plan_id, which is
      // exactly what the swap leaves behind.) Between its clear and its re-add the cart is empty,
      // our fetch hook wakes this sync into that gap, and a page showing five boxes was reset to
      // zero roughly a second after it painted them - the "it shows for a split second and then
      // disappears" report. The earlier guard keyed off a bfcache restore window, which is why it
      // missed this: coming back from checkout is usually a fresh load, not a restore.
      //
      // Retrying instead of time-boxing keeps the other case honest too. Emptying the bundle from
      // the cart drawer is a real event that must still reach the page; it simply has to survive a
      // second look, ~2.4s later at the outside, by which time any clear/re-add pair has landed.
      // Nothing above this point has been written down yet, and that ordering is the fix. The
      // rejected read used to run rememberOurKeys() first, so a cart caught mid-revert taught the
      // page that it owned NO lines. removeOurLines([]) then returned null, which skipped the
      // leftover sweep below it, so the next /cart/add.js stacked on top of the five boxes still
      // sitting in the cart - the page said one box while the bubble said four. A read we do not
      // believe must not be allowed to leave anything behind.
      // Inside the handoff window an empty cart is never acted on, however many times it comes
      // back that way. This is the part the retry budgets could not do: they had to guess when to
      // give up, and giving up meant wiping a bundle that was still in the cart. Here there is
      // nothing to guess - the customer pressed checkout seconds ago with these boxes, so an empty
      // read is Juo's revert, not a decision. The window is short and any real read ends it.
      if (!total && totalBoxes() > 0 && Date.now() < trustPersistedUntil) {
        scheduleCartSync(500);
        return;
      }

      if (!total && totalBoxes() > 0) {
        // Someone is mid-rewrite, so this read is not evidence of anything. Re-check WITHOUT
        // spending the retry budget - the budget exists to stop us arguing with a cart the
        // customer really did empty, and that is a different situation from one being rebuilt
        // underneath us. The fixed budget alone was the bug: Juo's ~900ms clear/re-add could
        // produce more empty reads than it allowed, and the read after the last one was taken
        // at face value, zeroing a bundle that was still in the cart.
        const rewriteInProgress = pendingCartWrites > 0 || (Date.now() - lastCartWriteAt) < 1500;
        if (rewriteInProgress && suspectEmptyReads < 20) {
          suspectEmptyReads += 1;
          scheduleCartSync(400);
          return;
        }
        if (!rewriteInProgress && emptyReadRetries < 3) {
          emptyReadRetries += 1;
          scheduleCartSync(400 * emptyReadRetries);
          return;
        }
      }
      if (total) {
        emptyReadRetries = 0;
        suspectEmptyReads = 0;
        // The cart answered with our lines in it, so it is trustworthy again and the handoff is
        // over. Re-stamped without the flag so a later reload does not re-enter the window.
        if (trustPersistedUntil) {
          trustPersistedUntil = 0;
          persistState();
        }
      }

      rememberOurKeys(cart);
      // Keep the cached cart current from reads as well as writes, so it stays a picture of the
      // real cart rather than only of the last thing this page sent.
      lastWrittenCart = cart;
      const changed = signature !== lastCartSignature;
      lastCartSignature = signature;

      if (isLive) {
        // Only take over when OUR lines moved. The page does not write to the cart until checkout,
        // so a customer can be mid-build with an empty cart; a drawer edit on some unrelated
        // product would then read back all-zeros and wipe the bundle they were assembling.
        // Comparing against the last cart we saw keeps that edit invisible here, while a real
        // change to a bundle line - including removing the last one - still applies.
        if (!changed) return;
      } else if (!total) {
        // First load with nothing of ours in the cart: leave the page on its defaults.
        return;
      }

      // Captured before the assign: what the PAGE was showing when this read landed. A cart that
      // disagrees is a snapshot from a different moment, not a newer truth.
      const pageQtyBeforeApply = {};
      cfg.flavours.forEach((flavour) => { pageQtyBeforeApply[flavour.key] = state.qty[flavour.key] || 0; });
      const cartMatchesPage = cfg.flavours.every(
        (flavour) => (quantities[flavour.key] || 0) === pageQtyBeforeApply[flavour.key]
      );

      Object.assign(state.qty, quantities);
      // The purchase mode comes from the cart's lines; the customer's CHOICE only from the opt-out
      // attribute, which is written by a click (this page's Eenmalige aankoop, or the drawer switch)
      // and never inferred. One-time lines without it are not a "no": they are what the cart holds
      // below the unlock count, an older write that raced a quick burst of clicks, or a box added
      // on a product page - and the drawer converts them anyway. Treating them as a decision is
      // what latched the page onto one-time. A returning one-time customer still keeps one-time:
      // their write carried the opt-out, so the cart says so.
      if (total) {
        const declined = !!(cart.attributes && cart.attributes._cowmit_sub_optout === '1');
        if (planId) {
          state.purchase = 'subscription';
          // A plan with no opt-out means subscription is on. If the page remembered a "no", the
          // customer has since switched it back on in the drawer.
          if (purchaseChoice === 'one-time' && !declined) purchaseChoice = null;
        } else if (declined && (!isLive || cartMatchesPage)) {
          // A live read must agree with the quantities the page shows before it is believed - it
          // can be carrying an earlier write from a quick burst of clicks.
          state.purchase = 'one-time';
          state.planId = null;
          purchaseChoice = 'one-time';
        }
      }
      render();

      // Applied AFTER render(): renderFrequency rebuilds the option list for the restored box
      // count, and only then does the saved plan exist as an option to select.
      // Matched by id first, then by WEEKS. Juo carries two plans per interval (the recurring one and
      // its fixed twin), and the cart drawer's switch (assets/cowmit-cart.js) picks the recurring one,
      // which need not be the one this page listed. Matching by id alone left the dropdown on its
      // default when the customer picked, say, 5 weeks in the drawer - and the next write from this
      // page would have put the default back into the cart.
      if (planId && intervalSelect && state.purchase === 'subscription') {
        const options = Array.from(intervalSelect.options);
        const match = options.find((option) => Number(option.value) === planId)
          || (planWeeks ? options.find((option) => Number(option.dataset.weeks) === planWeeks) : null);
        if (match) {
          intervalSelect.value = match.value;
          state.planId = Number(match.value) || null;
          syncIntervalDropdown();
          render();
        }
      }

      // Having just taken the page's state FROM the cart, the two agree - so checkout can use its
      // fast path for a customer who returns and buys without changing anything, instead of paying
      // two round trips to rewrite the cart into the state it is already in. Compared rather than
      // assumed: the restored quantities can still leave the purchase mode or plan differing from
      // what the cart lines carry, and then the cart is NOT what checkout would send.
      lastWriteOk = stateSignature() === signature;
    }

    // ---- Live page -> cart writes (client 2026-08-28) ---------------------------------------
    // The page used to write the cart ONLY at checkout, so building a bundle left the drawer
    // showing the previous write - add two boxes and the cart still held the old ones until you
    // checked out again. Every builder change now rewrites the cart, debounced, using the same
    // remove-then-add sequence checkout already used.
    let writeTimer = null;
    let writeChain = Promise.resolve();
    // How many writes are actually in flight, and whether we are mid-DOM-swap. Together with a
    // pending writeTimer these say "the page is mid-edit, ignore what the cart currently says".
    let inFlightWrites = 0;
    let refreshingUi = false;
    let lastWriteOk = false;
    // Set the moment a checkout begins and left set until the page is actually replaced. The
    // handoff writes the cart OUTSIDE writeCart() - the Juo swap clears and re-adds directly - so
    // inFlightWrites never rises for it and every guard built on that counter was blind to it.
    // Consequence, and the exact fault the client reported: /cart/clear.js woke the cart watcher,
    // 250ms later syncFromCart read the cart in the window between the clear and the re-add, saw
    // no lines of ours, and reset the builder - quantities to zero and the purchase mode from
    // Automatische levering back to Eenmalige aankoop - all while the customer was still looking at
    // the page waiting for the slow /checkout navigation. The bfcache then stored THAT wiped page,
    // which is why coming back showed an empty bundle.
    let checkoutHandoff = false;

    // Tells the cart drawer's script (assets/cowmit-cart.js) when this page is rewriting the cart.
    // A write here is remove-then-add, so for ~1s the cart holds none of our boxes. The drawer
    // used to look inside that gap, take it for an emptied cart, clear the customer's opt-out -
    // and then switch the re-added boxes to a subscription, ~7s after they chose Eenmalige aankoop
    // (reproduced on the preview). The checkout swap rewrites the cart the same way. The drawer
    // now waits for "idle" and acts once. `refreshed` = this page has already re-rendered the
    // drawer with the finished cart, so the drawer need not fetch it again.
    let announcedBusy = false;
    function announceCartBusy(refreshed) {
      const busyNow = inFlightWrites > 0 || checkoutHandoff;
      if (busyNow === announcedBusy) return;
      announcedBusy = busyNow;
      window.cowmitBundleBusy = busyNow;
      document.dispatchEvent(new CustomEvent('cowmit:bundle-busy', {
        detail: { busy: busyNow, refreshed: !busyNow && !!refreshed }
      }));
    }

    // The shape syncFromCart derives from the cart, computed instead from the page's own state.
    // After we write, recording this as the last-seen cart makes our own write invisible to the
    // cart -> page watcher: it reads back, computes the same signature, sees no change and leaves
    // the page alone. Without it a write landing while the customer keeps clicking would drag the
    // page back to the quantity that was in flight.
    function stateSignature() {
      const quantities = {};
      cfg.flavours.forEach((flavour) => { quantities[flavour.key] = state.qty[flavour.key] || 0; });
      const planId = state.purchase === 'subscription' && state.planId ? Number(state.planId) : null;
      return JSON.stringify(quantities) + '|' + String(planId);
    }

    // Writing the cart is not enough: /cart/update.js and /cart/add.js change the cart DATA, but
    // the header bubble and the drawer are Liquid-rendered markup that only refreshes when the
    // theme is told to re-render them. Without this the cart was correct while the UI kept showing
    // the previous quantity - the page said 4 and the bubble said 6.
    // Same call and same method the theme's own referral script uses (layout/theme.liquid), so the
    // markup is produced by the theme rather than assembled here.
    // The swap itself, for sections that arrive either from the fetch below or already rendered in a
    // cart write's answer (performWrite asks /cart/add.js for them).
    function applyCartSections(sections) {
        const parsed = (markup) => new DOMParser().parseFromString(markup || '', 'text/html');

        // Deliberately NOT cart-drawer's own renderContents(): on this theme that method ends by
        // opening the drawer, which is right for an add-to-cart button on a product page and very
        // wrong here - the drawer slid open on every +/- click. Swapping the markup ourselves gives
        // the same refreshed contents with no open/close behaviour attached.
        const bubble = document.querySelector('#cart-icon-bubble');
        const freshBubble = parsed(sections['cart-icon-bubble']).querySelector('#shopify-section-cart-icon-bubble');
        if (bubble && freshBubble) bubble.innerHTML = freshBubble.innerHTML;

        const drawerDoc = parsed(sections['cart-drawer']);
        const freshInner = drawerDoc.querySelector('#CartDrawer');
        const liveInner = document.querySelector('#CartDrawer');
        if (freshInner && liveInner) liveInner.innerHTML = freshInner.innerHTML;

        // The empty/filled state lives as a class on the <cart-drawer> element itself, so a cart
        // going from empty to filled needs it copied across or the drawer keeps rendering empty.
        // "active" is stripped and then restored from what the customer had: it is the class that
        // shows the drawer, and inheriting it from a fresh render is what would pop it open.
        const freshOuter = drawerDoc.querySelector('cart-drawer');
        const liveOuter = document.querySelector('cart-drawer');
        if (freshOuter && liveOuter) {
          const wasOpen = liveOuter.classList.contains('active');
          liveOuter.className = freshOuter.className.replace(/\bactive\b/g, '').replace(/\s+/g, ' ').trim();
          if (wasOpen) liveOuter.classList.add('active');
        }
        return !!(freshInner && liveInner);
    }

    async function refreshCartUi(rendered) {
      try {
        refreshingUi = true;
        if (rendered && rendered['cart-drawer']) return applyCartSections(rendered);
        const response = await fetch('/cart?sections=cart-drawer,cart-icon-bubble');
        if (!response.ok) return false;
        return applyCartSections(await response.json());
      } catch (error) {
        // A stale bubble is cosmetic; never let it break the write that already succeeded.
        return false;
      } finally {
        // Released a beat later: replacing the drawer markup can wake other cart listeners on the
        // page, whose callbacks are debounced and would otherwise land just after this flag cleared.
        setTimeout(() => { refreshingUi = false; }, 600);
      }
    }

    async function writeCart(options) {
      // Claimed UP FRONT, not after the write succeeds. Our own requests wake the cart -> page
      // watcher, and if the write throws part-way (the removal lands, the add does not) the
      // watcher would read a cart emptied of our lines, see a change, and wipe the customer's
      // selections off the page. The page is the source of truth here, so it keeps its state and
      // the next action - or checkout, which always rewrites - repairs the cart.
      lastCartSignature = stateSignature();
      // Cleared before the attempt: the signature above is claimed optimistically to protect the
      // page from a half-finished write, so on its own it is NOT evidence the cart matches. This
      // flag is, and checkout uses the pair to decide whether it can skip writing entirely.
      lastWriteOk = false;
      inFlightWrites += 1;
      announceCartBusy();
      let refreshed = false;
      try {
        refreshed = await performWrite(options);
        lastWriteOk = true;
      } catch (error) {
        knownOurKeys = null;   // unknown cart state: the next write reads before it writes
        lastWrittenCart = null;
        lastAttributesSent = null;  // may not have landed: resend with the next write
        plansMapCache = null;  // keyed by line keys, which are now unknown
        throw error;
      } finally {
        inFlightWrites -= 1;
        announceCartBusy(refreshed);
      }
    }

    // Line keys of OUR lines in the cart, as last observed. null means "unknown, go and read".
    // Every cart response we already receive carries this, so keeping it costs no extra request
    // and saves the ~340ms /cart.js read at the front of each write.
    let knownOurKeys = null;
    // The full cart as the last write left it, straight from that write's own response.
    let lastWrittenCart = null;

    function rememberOurKeys(cartLike) {
      if (!cartLike || !Array.isArray(cartLike.items)) return;
      knownOurKeys = cartLike.items
        .filter((line) => ourVariantIds.has(String(line.variant_id)))
        .map((line) => line.key);
    }

    // ---- Choice shared with the cart drawer (client 2026-09-27) ----------------------------
    // The drawer's "Automatische levering" switch (assets/cowmit-cart.js) turns itself on once the
    // cart holds enough boxes - unless the customer said no. It cannot see this page's
    // purchaseChoice, so the decision travels with the cart as attributes (underscore = hidden):
    //   _cowmit_sub_optout "1"   one-time chosen while subscription WAS on offer; "" otherwise
    //   _cowmit_sub_lane   0/1/2 the lane of the chosen interval, so the drawer keeps it when the
    //                            box count changes
    // Without the opt-out, a customer who picked Eenmalige aankoop here would open the drawer on the
    // next page and find their bundle silently switched to a subscription.
    let lastAttributesSent = null;

    function sharedAttributes() {
      // Kept below the unlock count too, so a "no" survives a detour to 1 box - in the drawer as well
      // as here. At 0 boxes the choice is cleared, and with it the opt-out.
      const optedOut = purchaseChoice === 'one-time' && totalBoxes() > 0;
      const attributes = { _cowmit_sub_optout: optedOut ? '1' : '' };
      if (state.purchase === 'subscription') {
        const lane = selectedFrequencyOption()?.dataset.lane;
        if (lane !== undefined) attributes._cowmit_sub_lane = lane;
      }
      return attributes;
    }

    // attributes ride along on the removal call, so sharing the choice costs no extra request -
    // except when there is nothing of ours to remove, and then only when they actually changed.
    async function removeOurLines(keys, attributes) {
      const updates = {};
      keys.forEach((key) => { updates[key] = 0; });
      if (!Object.keys(updates).length && !attributes) return null;
      const body = { updates };
      if (attributes) body.attributes = attributes;
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (!response.ok) throw new Error('clear');
      // update.js answers with the WHOLE cart, so this doubles as a free read-back.
      return await response.json().catch(() => null);
    }

    async function performWrite(options) {
      // Cold start only. Afterwards the keys come from responses we already receive.
      if (knownOurKeys === null) {
        const cart = await readCart();
        if (!cart || !Array.isArray(cart.items)) throw new Error('cart');
        rememberOurKeys(cart);
      }

      // Remove only OUR OWN lines, never /cart/clear.js - clearing destroyed the referral gift,
      // which the store-wide script then refused to re-grant for a year. Keyed by line KEY: a
      // variant can occupy several lines (different selling plan), and removal by variant id was
      // measured against the live store to clear only the FIRST of them.
      const attributes = sharedAttributes();
      const attributesKey = JSON.stringify(attributes);
      let afterRemoval = await removeOurLines(
        knownOurKeys,
        attributesKey !== lastAttributesSent ? attributes : null
      );
      lastAttributesSent = attributesKey;

      // Self-correcting: if the cached keys were stale - someone edited the drawer while we were
      // busy - the response still lists lines of ours. Clear those too rather than adding on top
      // of them, which would double the bundle. Costs a second call only when it actually happens.
      if (afterRemoval && Array.isArray(afterRemoval.items)) {
        const leftovers = afterRemoval.items
          .filter((line) => ourVariantIds.has(String(line.variant_id)))
          .map((line) => line.key);
        if (leftovers.length) afterRemoval = await removeOurLines(leftovers);
      }

      const payload = buildPayload();
      // The drawer and bubble come back rendered in the add's own answer, instead of costing a
      // separate /cart?sections= round trip after it (~0.4-0.7s measured). Not on checkout.
      const withUi = !(options && options.skipUiRefresh);
      const addBody = withUi ? Object.assign({}, payload, { sections: 'cart-drawer,cart-icon-bubble' }) : payload;
      let rendered = null;
      if (payload.items.length) {
        const addResponse = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(addBody)
        });
        if (!addResponse.ok) throw new Error('cart');
        let added = await addResponse.json().catch(() => null);

        // Verify what actually landed, rather than assuming the removal above did its job.
        // /cart/add.js answers with the WHOLE cart, so this costs nothing when the write was
        // clean, and it is the only thing standing between a stale key cache and a cart that
        // silently holds more than the page is showing. If our variants add up to more than we
        // asked for, lines survived the removal and the add stacked on top of them: clear every
        // line of ours and put the intended payload back exactly once.
        const intended = payload.items.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
        if (added && Array.isArray(added.items)) {
          const ours = added.items.filter((line) => ourVariantIds.has(String(line.variant_id)));
          const actual = ours.reduce((sum, line) => sum + (Number(line.quantity) || 0), 0);
          if (actual !== intended) {
            await removeOurLines(ours.map((line) => line.key));
            const retry = await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(addBody)
            });
            if (!retry.ok) throw new Error('cart');
            added = await retry.json().catch(() => null);
          }
        }

        knownOurKeys = added && Array.isArray(added.items)
          ? added.items.filter((line) => ourVariantIds.has(String(line.variant_id)))
              .map((line) => line.key)
          : null;
        // /cart/add.js answers with the WHOLE cart, so the checkout handoff no longer has to open
        // its own /cart.js read to find out what it just wrote. That read was a full round trip
        // (~300ms measured) sitting at the front of the critical path, spent re-learning the reply
        // we already had in hand.
        lastWrittenCart = added && Array.isArray(added.items) ? added : null;
        rendered = added && added.sections ? added.sections : null;
      } else {
        knownOurKeys = [];
        lastWrittenCart = afterRemoval && Array.isArray(afterRemoval.items) ? afterRemoval : null;
      }

      // Skipped on checkout: the customer is leaving this page, so repainting the drawer and the
      // header bubble is a ~470ms round trip nobody ever sees.
      if (!withUi) return false;
      return refreshCartUi(rendered);
    }

    // Debounced and SEQUENCED. Debounced so holding + does not fire a write per click; sequenced
    // through one promise chain so two writes can never overlap and land out of order, which would
    // leave the cart holding an older quantity than the page.
    // Start a queued write NOW instead of waiting out the debounce. Bound to the checkout button's
    // hover and touchstart: pointing at a button precedes clicking it by 100-300ms, and that head
    // start is free - the write has to happen either way, so beginning it early only means less of
    // it is left when the click arrives.
    function flushCartWrite() {
      if (!writeTimer) return;
      clearTimeout(writeTimer);
      writeTimer = null;
      writeChain = writeChain.then(writeCart).catch(() => {});
    }

    function scheduleCartWrite() {
      if (writeTimer) clearTimeout(writeTimer);
      writeTimer = setTimeout(() => {
        writeTimer = null;
        // A failed write is not fatal: the page stays the source of truth and checkout rewrites
        // the cart before handing off, so the customer is never charged from a half-written cart.
        writeChain = writeChain.then(writeCart).catch(() => {});
      }, 200);
    }

    // ---- Live cart -> page sync (client 2026-08-28) ----------------------------------------
    // Reflect drawer edits without a reload. Three independent triggers because the drawer's code
    // lives in the remote js.shrinetheme.com bundle, which we cannot read or version-pin - if one
    // hook is absent the others still fire. All funnel through one debounce so a single edit does
    // not cause three reads.
    let cartSyncTimer = null;
    let syncInFlight = false;

    function scheduleCartSync(delay) {
      // Cheaper than letting it fire and bail: the handoff makes several cart writes in a row and
      // each one would otherwise arm a timer and a /cart.js read we are about to throw away.
      if (checkoutHandoff) return;
      if (cartSyncTimer) clearTimeout(cartSyncTimer);
      cartSyncTimer = setTimeout(() => {
        cartSyncTimer = null;
        syncFromCart(true);
      }, delay || 250);
    }

    function watchCart() {
      // 0. The cart drawer's subscription switch (assets/cowmit-cart.js). It writes through the fetch it
      //    captured at load, which the hook below never sees, and announces each finished change.
      document.addEventListener('cowmit:cart-changed', () => scheduleCartSync());

      // 1. The theme's own pubsub, when the bundle exposes it globally.
      if (typeof window.subscribe === 'function') {
        try { window.subscribe('cart-update', () => scheduleCartSync()); } catch (_error) { /* theme pubsub optional */ }
      }

      // 2. Any cart write, whoever makes it. The drawer's remove button and quantity stepper POST
      //    to /cart/change.js; intercepting at the transport catches them without depending on the
      //    remote bundle's internals. Wrapped so a throw here can never break someone else's fetch.
      const nativeFetch = window.fetch;
      if (typeof nativeFetch === 'function' && !window.cowmitBundleCartWatch) {
        window.cowmitBundleCartWatch = true;
        window.fetch = function () {
          const result = nativeFetch.apply(this, arguments);
          try {
            const first = arguments[0];
            const url = String((first && first.url) || first || '');
            if (/\/cart\/(add|change|update|clear)/.test(url)) {
              // Counted from the moment it is SENT, not when it lands. Whoever is rewriting the
              // cart - us, the drawer, or Juo's revert - the window in which the cart is a
              // half-finished picture opens here, and a read taken inside it means nothing.
              pendingCartWrites += 1;
              // /cart/change.js and /cart/update.js answer with the whole cart - the drawer's +/- and
              // remove are change.js - so the page takes that answer as it is, instead of waiting
              // 250ms and reading /cart.js again (~0.9s per drawer click on the preview). Cloned in the
              // first handler on the promise, before the caller can read the body. Other writes, and
              // any answer that is not a cart, fall back to the debounced read as before.
              const answersWithCart = /\/cart\/(change|update)/.test(url);
              Promise.resolve(result)
                .then((response) => (answersWithCart && response && response.ok && typeof response.clone === 'function'
                  ? response.clone().json().catch(() => null)
                  : null), () => null)
                .then((cart) => {
                  pendingCartWrites = Math.max(0, pendingCartWrites - 1);
                  lastCartWriteAt = Date.now();
                  if (cart && Array.isArray(cart.items) && typeof cart.item_count === 'number' && !checkoutHandoff) {
                    if (cartSyncTimer) {
                      clearTimeout(cartSyncTimer);
                      cartSyncTimer = null;
                    }
                    syncFromCart(true, cart);
                  } else {
                    scheduleCartSync();
                  }
                })
                .catch(() => {
                  pendingCartWrites = Math.max(0, pendingCartWrites - 1);
                  scheduleCartSync();
                });
            }
          } catch (_error) { /* ignore malformed fetch args */ }
          return result;
        };
      }

      // A MutationObserver on the drawer used to sit here as a fallback for a theme that writes
      // with XMLHttpRequest. REMOVED: this theme demonstrably uses fetch (measured - the hook above
      // catches every /cart/change the drawer makes), so the observer caught nothing new and cost a
      // great deal. It fired on the drawer's OWN re-render, so each click in the drawer produced a
      // second read on top of the one from its change request: 3 clicks generated 12 /cart.js reads,
      // still arriving 3.6s after the last click, competing with the drawer's own requests. That
      // queueing is what made the drawer feel like it had to think between clicks.
    }

    function buildPayload() {
      const items = [];
      cfg.flavours.forEach((flavour) => {
        const quantity = state.qty[flavour.key];
        if (!quantity) return;
        const line = { id: flavour.variantId, quantity };
        if (state.purchase === 'subscription' && state.planId) line.selling_plan = state.planId;
        items.push(line);
      });
      return { items };
    }

    function setCheckoutBusy(busy) {
      // Both CTAs carry the same spinner/lock/label markup, so the swap has to run on whichever one
      // is on screen: the native button for one-time, <juo-checkout-link> for subscription. The juo
      // element never reaches its own data-state="processing" here - checkoutWithJuo intercepts the
      // click in the capture phase - so without this it sat completely inert during the cart write.
      [checkoutButton, juoCheckoutLink].forEach((target) => {
        if (!target) return;
        target.classList.toggle('is-busy', busy);
        const label = target.querySelector('[data-checkout-label]');
        if (!label) return;
        if (busy) {
          if (!label.dataset.idleLabel) label.dataset.idleLabel = label.textContent;
          if (labels.checkoutBusy) label.textContent = labels.checkoutBusy;
        } else if (label.dataset.idleLabel) {
          label.textContent = label.dataset.idleLabel;
        }
      });
      // "Bezig..." is not "Afrekenen" - different glyphs, different ink box, different correction.
      alignCheckoutLabels();
    }

    // ---- Optical centring of the checkout label (client 2026-09-08) -----------------------
    // align-items:center centres BOXES, and a text box is not centred on its own ink: the browser
    // centres the font's ascent+descent, while the visible glyphs occupy only part of that. For
    // Syne 800 20px "Afrekenen" the ink sits 1.5px ABOVE the box centre (ink ascent 15, ink descent
    // 0, against font ascent 16 / descent 4), so the word rode high of the lock beside it.
    //
    // This was first written as a flat `margin-top: 4px` in CSS and that was wrong, because the
    // correction is not a constant. It flips sign with the font - the same string in Suisselntl
    // sits 1.5px BELOW its box centre, so a downward nudge doubles the error rather than removing
    // it - and it changes with the text, which is a merchant setting: "Veilig afrekenen" carries a
    // descender in the g and needs a different value from "Afrekenen". A hardcoded number is only
    // ever right for the one font and the one word it was measured against.
    //
    // So measure the real thing instead: the label's own computed font, its own current text.
    let labelMetricsCtx = null;
    function alignCheckoutLabels() {
      [checkoutButton, juoCheckoutLink].forEach((target) => {
        if (!target) return;
        const label = target.querySelector('[data-checkout-label]');
        if (!label) return;
        const text = (label.textContent || '').trim();
        // Clear first: the measurement has to see the label where the cascade puts it, not where a
        // previous pass moved it.
        label.style.removeProperty('margin-top');
        label.style.removeProperty('margin-bottom');
        if (!text) return;

        const style = window.getComputedStyle(label);
        labelMetricsCtx = labelMetricsCtx || document.createElement('canvas').getContext('2d');
        if (!labelMetricsCtx) return;
        labelMetricsCtx.font = [
          style.fontStyle, style.fontWeight, style.fontSize, style.fontFamily
        ].join(' ');

        let metrics;
        try { metrics = labelMetricsCtx.measureText(text); } catch (_error) { return; }
        const fontAscent = metrics.fontBoundingBoxAscent;
        const fontDescent = metrics.fontBoundingBoxDescent;
        const inkAscent = metrics.actualBoundingBoxAscent;
        const inkDescent = metrics.actualBoundingBoxDescent;
        // Safari only shipped the fontBoundingBox* pair in 16.4; older engines return undefined and
        // the arithmetic would silently produce NaN, so leave the label alone rather than move it
        // by a garbage amount.
        if (![fontAscent, fontDescent, inkAscent, inkDescent].every(Number.isFinite)) return;

        // The baseline sits fontAscent below the content box top; the ink centre sits half the ink
        // height above the baseline. Positive means the ink is BELOW the box centre.
        const inkCentre = fontAscent - (inkAscent - inkDescent) / 2;
        const boxCentre = (fontAscent + fontDescent) / 2;
        const offset = inkCentre - boxCentre;

        // A flex item under align-items:center is centred by its MARGIN box, so a margin on one
        // side moves the border box by half of it. Round the shift to a whole pixel first and
        // double it, so the glyphs land on the pixel grid instead of being resampled across it -
        // at worst half a pixel out, which is finer than the artefact being corrected.
        const shift = Math.round(-offset);
        if (shift > 0) label.style.marginTop = (shift * 2) + 'px';
        else if (shift < 0) label.style.marginBottom = (-shift * 2) + 'px';
      });
    }

    async function ensureCartWritten() {
      const nothingPending = !writeTimer && inFlightWrites === 0;
      if (nothingPending && lastWriteOk && lastCartSignature === stateSignature()) {
        return;
      }
      if (writeTimer) { clearTimeout(writeTimer); writeTimer = null; }
      writeChain = writeChain.catch(() => {}).then(() => writeCart({ skipUiRefresh: true }));
      await writeChain;
    }

    async function fetchSellingPlansMap() {
      const response = await fetch('/?section_id=juo-selling-plans', {
        cache: 'no-store',
        headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' }
      });
      if (!response.ok) throw new Error('selling-plans-section');
      const html = await response.text();
      const wrap = document.createElement('div');
      wrap.innerHTML = html;
      const tpl = wrap.querySelector('template');
      const raw = (tpl?.content?.textContent || tpl?.textContent || tpl?.innerHTML || '{}').trim();
      return JSON.parse(raw || '{}');
    }

    // The map is line-KEY -> fixed selling plan id, rendered from whatever is in the cart at the
    // moment of the request, so it cannot be fetched before the cart is written and it goes stale
    // the instant the line keys change. Caching it against those keys is therefore the whole of
    // its validity condition - the same test Juo's own module applies to its copy.
    let plansMapCache = null;   // { keys, map }
    let plansMapInFlight = null;

    // Keyed on EVERY line in the cart, not just ours. The map covers whatever else the customer is
    // buying, so a change to some other subscription line invalidates it just as surely as a change
    // to a bundle line - caching against our keys alone would hand the swap a stale plan id for
    // that line. Falls back to null (meaning "do not cache") when the cart is unknown.
    function cartKeySignature() {
      const items = lastWrittenCart && Array.isArray(lastWrittenCart.items)
        ? lastWrittenCart.items
        : null;
      if (!items) return null;
      return items.map((line) => line.key).join(',');
    }

    function sellingPlansMapFor(keys) {
      if (keys && plansMapCache && plansMapCache.keys === keys) {
        return Promise.resolve(plansMapCache.map);
      }
      // One request at a time. The warm-up below and a fast click can otherwise both open one.
      if (plansMapInFlight && plansMapInFlight.keys === keys) return plansMapInFlight.promise;
      const promise = fetchSellingPlansMap().then((map) => {
        if (keys) plansMapCache = { keys, map };
        return map;
      }).finally(() => {
        if (plansMapInFlight && plansMapInFlight.promise === promise) plansMapInFlight = null;
      });
      plansMapInFlight = { keys, promise };
      return promise;
    }

    // Reaching for the button precedes pressing it by 100-300ms. flushCartWrite already spends that
    // head start on the cart write; this spends what is left of it on the section fetch that
    // follows, so by click time the only work still owed is the clear + re-add. Speculative and
    // therefore silent on failure: a warm-up that throws must not surface an error to a customer
    // who has not asked for anything yet, and the real call will simply fetch it again.
    function warmSellingPlansMap() {
      if (state.purchase !== 'subscription' || totalBoxes() === 0) return;
      Promise.resolve(writeChain).catch(() => {}).then(() => {
        if (checkoutHandoff) return;
        const keys = cartKeySignature();
        if (!keys) return;
        sellingPlansMapFor(keys).catch(() => {});
      });
    }

    function prepareCheckout() {
      flushCartWrite();
      warmSellingPlansMap();
    }

    async function juoSwapAndCheckout() {
      const checkoutUrl = '/checkout?skip_shop_pay=true';
      // ensureCartWritten() has just run, and the write's own /cart/add.js reply IS the cart. Only
      // pay for a read when there is no such reply to lean on - a returning customer whose cart
      // already matched the page took the fast path and never wrote.
      const cart = lastWrittenCart || await fetch('/cart.js', {
        cache: 'no-store',
        headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' }
      }).then((r) => {
        if (!r.ok) throw new Error('cart');
        return r.json();
      });

      // Keyed on the cart this swap is actually about to rewrite, so a warm-up made against a
      // different cart cannot be mistaken for a hit.
      const cartKeys = Array.isArray(cart.items) ? cart.items.map((line) => line.key).join(',') : null;
      const sellingPlansMap = await sellingPlansMapFor(cartKeys);
      const shouldSwitch = (cart.items || []).some(
        (item) => item.selling_plan_allocation != null && sellingPlansMap[item.key] != null
      );

      if (!shouldSwitch) {
        window.location.href = checkoutUrl;
        return;
      }

      const toAdd = cart.items.map((item) => {
        const mapped = item.selling_plan_allocation != null ? sellingPlansMap[item.key] : null;
        return {
          id: item.variant_id,
          quantity: item.quantity,
          properties: {
            ...(item.properties || {}),
            ...(item.selling_plan_allocation != null && mapped != null
              ? { _selling_plan_id: String(item.selling_plan_allocation.selling_plan.id) }
              : {})
          },
          ...(item.selling_plan_allocation != null
            ? {
                selling_plan:
                  mapped != null ? mapped : item.selling_plan_allocation.selling_plan.id
              }
            : {})
        };
      });

      const clearResponse = await fetch('/cart/clear.js', { method: 'POST' });
      if (!clearResponse.ok) throw new Error('clear');

      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: cart.attributes, items: toAdd })
      });
      if (!addResponse.ok) throw new Error('swap-add');

      window.location.href = checkoutUrl;
    }

    async function checkout() {
      if (!checkoutButton || totalBoxes() === 0) return;
      if (checkoutHandoff) return;   // second click while the first is still working
      checkoutHandoff = true;
      announceCartBusy();
      // Stamped now, while the state is still known-good, so the page can put itself back
      // together on return without waiting for a cart it cannot read yet.
      persistState({ handoff: true });
      checkoutButton.disabled = true;
      setCheckoutBusy(true);
      if (juoCheckoutLink) juoCheckoutLink.setAttribute('aria-disabled', 'true');
      if (checkoutError) checkoutError.hidden = true;

      try {
        // Fast path. Every builder change already writes the cart, so by the time anyone reaches
        // this button the cart is usually ALREADY what the page shows - and re-writing it cost
        // four sequential round trips (~2.2s measured on the live store) to arrive at the state it
        // was in before. Redirect straight away when the last write succeeded and nothing has
        // changed since. Both conditions are needed: the signature alone is claimed optimistically
        // and would still match after a write that failed.
        const nothingPending = !writeTimer && inFlightWrites === 0;
        if (nothingPending && lastWriteOk && lastCartSignature === stateSignature()) {
          window.location.href = '/checkout';
          return;
        }

        // Otherwise write once more so the cart provably matches the page before handing off.
        // Draining the queue first stops a write scheduled moments ago from landing after the
        // redirect and changing the cart underneath the checkout already on screen.
        if (writeTimer) { clearTimeout(writeTimer); writeTimer = null; }
        writeChain = writeChain.catch(() => {}).then(() => writeCart({ skipUiRefresh: true }));
        await writeChain;
        window.location.href = '/checkout';
      } catch (error) {
        checkoutHandoff = false;
        announceCartBusy(false);
        if (checkoutError) {
          checkoutError.textContent = labels.error || '';
          checkoutError.hidden = false;
        }
        setCheckoutBusy(false);
        checkoutButton.disabled = false;
        if (juoCheckoutLink) juoCheckoutLink.setAttribute('aria-disabled', 'false');
      }
    }

    async function checkoutWithJuo(event) {
      event.preventDefault();
      event.stopImmediatePropagation();

      if (state.purchase !== 'subscription' || totalBoxes() === 0) return;
      if (checkoutHandoff) return;   // second click while the first is still working

      if (!state.planId && intervalSelect) {
        state.planId = Number(intervalSelect.value) || null;
      }
      if (!state.planId) {
        if (checkoutError) {
          checkoutError.textContent = labels.error || '';
          checkoutError.hidden = false;
        }
        return;
      }

      checkoutHandoff = true;
      announceCartBusy();
      persistState({ handoff: true });
      checkoutButton.disabled = true;
      setCheckoutBusy(true);
      if (juoCheckoutLink) juoCheckoutLink.setAttribute('aria-disabled', 'true');
      if (checkoutError) checkoutError.hidden = true;

      try {
        // Keep the cart in sync with the builder, then run the fixed-plan swap and
        // redirect to local-payments checkout (same path as <juo-checkout-link>).
        await ensureCartWritten();
        await juoSwapAndCheckout();
      } catch (error) {
        console.error('Cowmit Juo checkout failed:', error);
        checkoutHandoff = false;
        announceCartBusy(false);
        if (checkoutError) {
          checkoutError.textContent = labels.error || '';
          checkoutError.hidden = false;
        }
        setCheckoutBusy(false);
        checkoutButton.disabled = false;
        if (juoCheckoutLink) juoCheckoutLink.setAttribute('aria-disabled', 'false');
        // The swap may have left the cart half-rewritten. Read it back now that syncing is
        // allowed again, so the page shows what the customer would actually be buying.
        scheduleCartSync(0);
      }
    }

    function updateQuantity(key, nextQuantity) {
      if (!Object.prototype.hasOwnProperty.call(state.qty, key)) return;
      const previousPurchase = state.purchase;
      state.qty[key] = Math.max(0, Math.min(9, nextQuantity));
      render();
      persistState();
      scheduleCartWrite();
      // Crossing the subscription threshold can change purchase mode. Do not
      // leave the drawer showing the previous mode during the normal debounce.
      if (state.purchase !== previousPurchase) flushCartWrite();
    }

    function closeIntervalMenu() {
      if (!intervalMenu || !intervalToggle || !intervalDropdown) return;
      intervalMenu.hidden = true;
      intervalToggle.setAttribute('aria-expanded', 'false');
      intervalDropdown.classList.remove('is-open');
    }

    function syncIntervalDropdown() {
      if (!intervalSelect) return;
      intervalMenu?.querySelectorAll('[data-interval-option]').forEach((option) => {
        const isSelected = option.dataset.value === intervalSelect.value;
        option.classList.toggle('is-selected', isSelected);
        option.setAttribute('aria-selected', String(isSelected));
      });
    }

    function buildIntervalDropdown() {
      if (!intervalSelect || !intervalMenu) return;
      intervalMenu.replaceChildren();
      Array.from(intervalSelect.options).forEach((selectOption) => {
        const option = document.createElement('button');
        option.className = 'cowmit-bundle__interval-option';
        option.type = 'button';
        option.dataset.intervalOption = '';
        option.dataset.value = selectOption.value;
        option.setAttribute('role', 'option');
        option.textContent = selectOption.textContent;
        option.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          intervalSelect.value = option.dataset.value;
          state.planId = Number(intervalSelect.value) || null;
          syncIntervalDropdown();
          closeIntervalMenu();
          render();
          scheduleCartWrite();
        });
        intervalMenu.appendChild(option);
      });
      syncIntervalDropdown();
    }

    root.querySelectorAll('[data-flavour-row]').forEach((row) => {
      const key = row.dataset.key;
      row.querySelector('[data-add-btn]')?.addEventListener('click', () => updateQuantity(key, 1));
      row.querySelector('[data-qty-plus]')?.addEventListener('click', () => updateQuantity(key, state.qty[key] + 1));
      row.querySelector('[data-qty-minus]')?.addEventListener('click', () => updateQuantity(key, state.qty[key] - 1));
    });


    root.querySelectorAll('[data-purchase-option] input[type="radio"]').forEach((radio) => {
      radio.addEventListener('change', () => {
        if (!radio.checked) return;
        purchaseChoice = radio.value;
        state.purchase = radio.value;
        if (state.purchase === 'subscription' && intervalSelect) {
          state.planId = Number(intervalSelect.value) || null;
        }
        render();
        persistState();
        // One-time and subscription are different cart lines, so the cart has to be rewritten.
        scheduleCartWrite();
        flushCartWrite();
      });
    });

    if (summaryToggle && summaryPanel) {
      summaryToggle.addEventListener('click', () => {
        const willOpen = summaryPanel.hidden;
        summaryPanel.hidden = !willOpen;
        summary?.classList.toggle('is-open', willOpen);
        summaryToggle.setAttribute('aria-expanded', String(willOpen));
      });
    }

    const plans = cfg.flavours.flatMap((flavour) => flavour.sellingPlans || []);
    const uniquePlans = [];
    const seenPlanIds = new Set();
    plans.forEach((plan) => {
      const key = String(plan.id);
      if (seenPlanIds.has(key)) return;
      seenPlanIds.add(key);
      uniquePlans.push(plan);
    });

    if (intervalSelect) {
      intervalSelect.addEventListener('change', () => {
        state.planId = Number(intervalSelect.value) || null;
        syncIntervalDropdown();
        render();
        persistState();
        scheduleCartWrite();
      });
    }

    intervalToggle?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (!intervalMenu || !intervalDropdown) return;
      const willOpen = intervalMenu.hidden;
      closeIntervalMenu();
      if (willOpen) {
        intervalMenu.hidden = false;
        intervalToggle.setAttribute('aria-expanded', 'true');
        intervalDropdown.classList.add('is-open');
      }
    });

    root.addEventListener('click', (event) => {
      if (!intervalDropdown?.contains(event.target)) closeIntervalMenu();
    });

    root.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeIntervalMenu();
    });

    window.addEventListener('resize', alignIntervalToSummary, { passive: true });
    // Re-measure once the web fonts land: the first pass runs against the fallback face, whose text
    // is narrower, so the control was sized ~23px short until something else triggered a re-render.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(alignIntervalToSummary).catch(() => {});
      // The first pass runs against the fallback face, whose metrics are not Syne's - and the
      // correction flips sign between the two, so it MUST be recomputed once the real face lands.
      document.fonts.ready.then(alignCheckoutLabels).catch(() => {});
    }
    alignCheckoutLabels();

    checkoutButton?.addEventListener('click', checkout);
    juoCheckoutLink?.addEventListener('click', checkoutWithJuo, true);
    // Pre-commit: begin any queued cart write - and, for a subscription, warm the selling-plans
    // section the swap needs - the moment the customer reaches for either button.
    checkoutButton?.addEventListener('pointerenter', prepareCheckout);
    juoCheckoutLink?.addEventListener('pointerenter', prepareCheckout);
    checkoutButton?.addEventListener('touchstart', prepareCheckout, { passive: true });
    juoCheckoutLink?.addEventListener('touchstart', prepareCheckout, { passive: true });
    // Before the first paint, so the page never flashes an empty bundle it is about to refill,
    // and so the empty-read guards below have something to protect from the very first read.
    restorePersisted();
    render();
    syncFromCart(false);
    watchCart();

    // Coming BACK to the page needs its own hook. A normal navigation re-runs init, but the back
    // button often restores from the bfcache with the old JS state intact and no re-init at all -
    // which is the exact journey the client reported (checkout, then back). event.persisted marks
    // that case; re-reading the cart also picks up any edit made in the drawer meanwhile.
    window.addEventListener('pageshow', (event) => {
      if (!event.persisted) return;
      // The page was frozen mid-handoff, so it comes back wearing the busy state and with syncing
      // switched off. Both have to be undone before the read, or the button stays stuck on
      // "Bezig..." and the read is discarded by its own guard.
      // The page is coming back from a checkout it launched, so open the same trust window a
      // fresh load would get from the stored handoff flag. initBundle does not re-run on a
      // bfcache restore, so restorePersisted() never fires here and the window would otherwise
      // stay shut for exactly the journey it exists to cover.
      if (checkoutHandoff) trustPersistedUntil = Date.now() + HANDOFF_TRUST_MS;
      checkoutHandoff = false;
      announceCartBusy(false);
      setCheckoutBusy(false);
      if (checkoutButton) checkoutButton.disabled = totalBoxes() === 0;
      if (juoCheckoutLink) {
        juoCheckoutLink.setAttribute('aria-disabled', totalBoxes() === 0 ? 'true' : 'false');
      }
      // Anything the checkout swap wrote is stale the moment the customer turns back, and Juo's
      // own module reverts that swap from its pageshow handler - a clear plus a re-add. Reading
      // before it finishes would catch the cart empty between the two, which is the same trap the
      // handoff guard exists for. Cached copies are dropped so the read is a real one.
      lastWrittenCart = null;
      plansMapCache = null;
      knownOurKeys = null;
      lastCartSignature = null;
      lastAttributesSent = null;
      emptyReadRetries = 0;
      failedReadRetries = 0;
      suspectEmptyReads = 0;
      scheduleCartSync(700);
    });
  }

  function init(scope = document) {
    if (scope.matches?.('[data-bundle-root]')) initBundle(scope);
    scope.querySelectorAll('[data-bundle-root]').forEach(initBundle);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
